/**
 * 繰り返し支払い(recurring_costs。旧: 固定費マスタ)の読み書き。
 *
 * 家賃・水道光熱費・リースなど、決まった周期で支払うもの。各回は支払い待ちとして自動で作られ、
 * 経理の支払い→承認者の完了確認を通る(2026-09-07)。金額改定は履歴を残し、指定月以降に反映する。
 */
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  Timestamp,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { isValidAmount } from '@core/lib/money';
import { writeAuditLog } from '@core/lib/auditLog';
import type {
  AmountRevision,
  AuditLog,
  DepartmentId,
  HolidayShift,
  MonthStr,
  PaymentMethod,
  PaymentRule,
  PostingRule,
  RecurringCost,
  RecurringFrequency,
  Yen,
} from '@core/lib/types';

const COLLECTION = 'recurring_costs';

function withId(snap: QueryDocumentSnapshot): RecurringCost {
  return { id: snap.id, ...(snap.data() as object) } as RecurringCost;
}

export async function fetchRecurringCosts(includeInactive = true): Promise<RecurringCost[]> {
  const snap = await getDocs(query(collection(getDb(), COLLECTION), orderBy('name')));
  return snap.docs.map(withId).filter((r) => includeInactive || r.active);
}

export type RecurringCostInput = {
  name: string;
  companyId: string;
  departmentId: DepartmentId;
  hierarchyNodeId: string | null;
  accountId: string;
  subAccountId: string;
  amount: Yen;
  paymentMethod: PaymentMethod;
  cashAccountId: string | null;
  postingRule: PostingRule;
  paymentRule: PaymentRule;
  /** 毎月 / 毎週(2026-09-07) */
  frequency: RecurringFrequency;
  /** 支払日が土日祝のとき前後どちらの平日にずらすか */
  holidayShift: HolidayShift;
  /** 毎回金額が違う(amount は目安) */
  amountVaries: boolean;
  startMonth: MonthStr;
  endMonth: MonthStr | null;
  counterparty: string;
  description: string;
  active: boolean;
};

function validate(input: RecurringCostInput) {
  if (!input.name.trim()) throw new Error('名称を入力してください');
  if (!isValidAmount(input.amount)) throw new Error('金額は1円以上の整数で入力してください');
  if (!input.accountId) throw new Error('科目①を選んでください');
  if (!input.counterparty.trim()) throw new Error('支払先を入力してください');
  if (input.paymentMethod === 'card' && !input.cashAccountId) throw new Error('カードを選んでください');
  if (input.frequency.kind === 'monthly' && input.paymentRule.kind !== 'month_end' && input.paymentRule.day !== 'end'
    && !(input.paymentRule.day >= 1 && input.paymentRule.day <= 31)) {
    throw new Error('支払日は1〜31か末日を選んでください');
  }
  if (input.postingRule.kind === 'relative' && input.postingRule.day !== 'end'
    && !(input.postingRule.day >= 1 && input.postingRule.day <= 31)) {
    throw new Error('計上日の日にちは1〜31か末日を選んでください');
  }
  if (input.frequency.kind === 'weekly' && !(input.frequency.weekday >= 0 && input.frequency.weekday <= 6)) {
    throw new Error('曜日を選んでください');
  }
  if (!/^\d{4}-\d{2}$/.test(input.startMonth)) throw new Error('開始月を YYYY-MM で入力してください');
  if (input.endMonth && input.endMonth < input.startMonth) {
    throw new Error('終了月は開始月より後にしてください');
  }
}

function toDocument(input: RecurringCostInput) {
  return {
    name: input.name.trim(),
    company_id: input.companyId,
    department_id: input.departmentId,
    hierarchy_node_id: input.hierarchyNodeId,
    account_id: input.accountId,
    sub_account_id: input.subAccountId,
    amount: input.amount,
    payment_method: input.paymentMethod,
    cash_account_id: input.cashAccountId,
    posting_rule: input.postingRule,
    payment_rule: input.paymentRule,
    frequency: input.frequency,
    holiday_shift: input.holidayShift,
    amount_varies: input.amountVaries,
    start_month: input.startMonth,
    end_month: input.endMonth,
    counterparty: input.counterparty || null,
    description: input.description || null,
    active: input.active,
  };
}

/** 金額の改定(2026-09-08): 適用開始月と、その月からの金額 */
export type AmountRevisionInput = { amount: Yen; effectiveFrom: MonthStr };

function toRevisions(list: AmountRevisionInput[], userId: string): AmountRevision[] {
  return list.map((r) => {
    if (!isValidAmount(r.amount)) throw new Error('改定後の金額は1円以上の整数で入力してください');
    if (!/^\d{4}-\d{2}$/.test(r.effectiveFrom)) throw new Error('改定の適用開始月を選んでください');
    return { amount: r.amount, effective_from: r.effectiveFrom, changed_by: userId, changed_at: Timestamp.now() };
  });
}

function sortHistory(history: AmountRevision[]): AmountRevision[] {
  return [...history].sort((a, b) => (a.effective_from < b.effective_from ? -1 : a.effective_from > b.effective_from ? 1 : 0));
}

export async function createRecurringCost(
  input: RecurringCostInput,
  userId: string,
  revisions: AmountRevisionInput[] = [],
): Promise<void> {
  validate(input);
  const id = doc(collection(getDb(), COLLECTION)).id;
  const now = serverTimestamp();
  const revision: AmountRevision = {
    amount: input.amount,
    effective_from: input.startMonth,
    changed_by: userId,
    changed_at: Timestamp.now(),
  };

  await setDoc(doc(getDb(), COLLECTION, id), {
    ...toDocument(input),
    amount_history: sortHistory([revision, ...toRevisions(revisions, userId)]),
    created_by: userId,
    created_at: now,
    updated_by: userId,
    updated_at: now,
  });

  await writeAuditLog({
    targetType: 'master',
    targetId: id,
    action: 'recurring-cost-create',
    by: userId,
    after: { name: input.name, amount: input.amount },
  });
}

/**
 * 更新。金額が変わった場合は改定履歴を追加する。
 * **改定は翌月以降に反映**する(当月分は既に計上されている可能性があるため)。
 */
export async function updateRecurringCost(params: {
  id: string;
  input: RecurringCostInput;
  current: RecurringCost;
  effectiveFrom: MonthStr;
  userId: string;
  /** 「改定を追加」で入れた将来の改定(2026-09-08) */
  revisions?: AmountRevisionInput[];
  /** 変更内容の説明(画面側で作る。変更履歴の表示に使う・2026-09-08) */
  changes?: string[];
}): Promise<void> {
  const { id, input, current, effectiveFrom, userId, revisions = [], changes = [] } = params;
  validate(input);

  const amountChanged = current.amount !== input.amount;
  const history = [...(current.amount_history ?? [])];
  if (amountChanged) {
    history.push({
      amount: input.amount,
      effective_from: effectiveFrom,
      changed_by: userId,
      changed_at: Timestamp.now(),
    });
  }
  history.push(...toRevisions(revisions, userId));

  await updateDoc(doc(getDb(), COLLECTION, id), {
    ...toDocument(input),
    amount_history: sortHistory(history),
    updated_by: userId,
    updated_at: serverTimestamp(),
  });

  await writeAuditLog({
    targetType: 'master',
    targetId: id,
    action: amountChanged ? 'recurring-cost-amount-change' : 'recurring-cost-update',
    by: userId,
    before: { amount: current.amount },
    after: { amount: input.amount, effective_from: effectiveFrom, changes },
  });
}

export async function setRecurringCostActive(id: string, active: boolean, userId?: string): Promise<void> {
  await updateDoc(doc(getDb(), COLLECTION, id), {
    active,
    updated_at: serverTimestamp(),
    ...(userId ? { updated_by: userId } : {}),
  });
  if (userId) {
    await writeAuditLog({
      targetType: 'master',
      targetId: id,
      action: active ? 'recurring-cost-activate' : 'recurring-cost-deactivate',
      by: userId,
      after: { active, changes: [active ? '有効にした' : '無効にした'] },
    });
  }
}

/** 変更履歴(監査ログ)。新しいものが先 */
export async function fetchRecurringAuditLogs(costId: string, max = 50): Promise<AuditLog[]> {
  const snap = await getDocs(
    query(collection(getDb(), 'audit_logs'), where('target_type', '==', 'master'), where('target_id', '==', costId)),
  );
  const at = (l: AuditLog) => (l.at && typeof (l.at as { toMillis?: () => number }).toMillis === 'function' ? l.at.toMillis() : 0);
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as object) }) as AuditLog)
    .sort((a, b) => at(b) - at(a))
    .slice(0, max);
}

/** 削除。過去に計上した取引は残る(取引側は固定費IDを保持している) */
export async function deleteRecurringCost(id: string, userId: string): Promise<void> {
  await deleteDoc(doc(getDb(), COLLECTION, id));
  await writeAuditLog({
    targetType: 'master',
    targetId: id,
    action: 'recurring-cost-delete',
    by: userId,
  });
}

/** その月に適用される金額を改定履歴から求める(履歴が無ければ現在の金額) */
export function amountForMonth(cost: RecurringCost, month: MonthStr): Yen {
  const applicable = (cost.amount_history ?? [])
    .filter((h) => h.effective_from <= month)
    .sort((a, b) => (a.effective_from < b.effective_from ? -1 : 1));
  return applicable.length > 0 ? applicable[applicable.length - 1]!.amount : cost.amount;
}

/** その月が適用期間内か */
export function isActiveInMonth(cost: RecurringCost, month: MonthStr): boolean {
  if (!cost.active) return false;
  if (month < cost.start_month) return false;
  if (cost.end_month && month > cost.end_month) return false;
  return true;
}
